// Hash routes: every screen and its filters/dates are in the URL, so they can be bookmarked (SPEC §9.4).

import type { Role } from "./api/types";
import { getUser } from "./session";

export interface RouteMatch {
  params: Record<string, string>;
  query: URLSearchParams;
  path: string;
}

export type PageRenderer = (container: HTMLElement, route: RouteMatch) => Promise<void>;

export interface Route {
  pattern: string;
  roles: Role[];
  render: PageRenderer;
}

export interface RouterPages {
  routes: Route[];
  signIn: PageRenderer;
  forbidden: PageRenderer;
  notFound: PageRenderer;
}

let pages: RouterPages | null = null;
let pageContainer: HTMLElement | null = null;
/**
 * Each navigation renders into its own slot inside the page container. A page that finishes loading after the user
 * has moved on writes into a slot that is no longer in the document, so it can't draw over the newer page.
 */
let activeSlot: HTMLElement | null = null;
let redraw: (() => void) | null = null;
let afterNavigation: () => void = (): void => undefined;

/** Starts listening to the hash and renders the current route. */
export function startRouter(container: HTMLElement, routerPages: RouterPages, onNavigated: () => void): void {
  pageContainer = container;
  pages = routerPages;
  afterNavigation = onNavigated;
  window.addEventListener("hashchange", (): void => {
    void renderCurrentRoute();
  });
  void renderCurrentRoute();
}

/** Goes to an app path such as "/groups/123?from=2026-10-12". */
export function navigate(path: string): void {
  const target: string = "#" + path;
  if (window.location.hash === target) {
    void renderCurrentRoute();
    return;
  }

  window.location.hash = target;
}

/** Replaces the query of the current path without adding a new screen. */
export function replaceQuery(values: Record<string, string>): void {
  const path: string = currentPath();
  const query: URLSearchParams = new URLSearchParams(values);
  window.history.replaceState(null, "", `#${path}?${query.toString()}`);
}

/** The path part of the hash, e.g. "/groups/123". */
export function currentPath(): string {
  const hash: string = window.location.hash.replace(/^#/, "");
  const path: string = hash.split("?")[0] ?? "";
  return path === "" ? "/" : path;
}

/** The full path with query, used to come back after signing in (SPEC §7.1). */
export function currentFullPath(): string {
  const hash: string = window.location.hash.replace(/^#/, "");
  return hash === "" ? "/" : hash;
}

/**
 * Registers how the page rendered into `container` redraws itself from data it already has. Called after a language
 * switch, so the screen re-renders without reloading data (SPEC §10.1). Ignored when that page is no longer current.
 */
export function setRedraw(container: HTMLElement, draw: () => void): void {
  if (container === activeSlot) {
    redraw = draw;
  }
}

/** False once the user has navigated away from the page rendered into `container`. */
export function isCurrentPage(container: HTMLElement): boolean {
  return container === activeSlot;
}

/** Redraws the current page in place, or renders it again when it registered no redraw. */
export function redrawCurrentPage(): void {
  if (redraw !== null) {
    redraw();
    return;
  }

  void renderCurrentRoute();
}

/** Renders the route for the current hash. */
export async function renderCurrentRoute(): Promise<void> {
  if (pages === null || pageContainer === null) {
    return;
  }

  redraw = null;
  const route: RouteMatch = parseHash();
  const renderer: PageRenderer = chooseRenderer(pages, route);
  const slot: HTMLDivElement = document.createElement("div");
  activeSlot = slot;
  pageContainer.replaceChildren(slot);
  window.scrollTo(0, 0);
  await renderer(slot, route);
  if (slot === activeSlot) {
    afterNavigation();
  }
}

function chooseRenderer(routerPages: RouterPages, route: RouteMatch): PageRenderer {
  if (route.path === "/sign-in") {
    return routerPages.signIn;
  }

  const user: ReturnType<typeof getUser> = getUser();
  if (user === null) {
    return routerPages.signIn;
  }

  for (const candidate of routerPages.routes) {
    const params: Record<string, string> | null = matchPattern(candidate.pattern, route.path);
    if (params !== null) {
      route.params = params;
      return candidate.roles.includes(user.role) ? candidate.render : routerPages.forbidden;
    }
  }

  return routerPages.notFound;
}

function parseHash(): RouteMatch {
  const hash: string = window.location.hash.replace(/^#/, "");
  const questionIndex: number = hash.indexOf("?");
  const query: string = questionIndex >= 0 ? hash.slice(questionIndex + 1) : "";
  return { params: {}, query: new URLSearchParams(query), path: currentPath() };
}

/** "/groups/:id" against "/groups/42" → { id: "42" }; null when it doesn't match. */
export function matchPattern(pattern: string, path: string): Record<string, string> | null {
  const patternParts: string[] = pattern.split("/");
  const pathParts: string[] = path.split("/");
  if (patternParts.length !== pathParts.length) {
    return null;
  }

  const params: Record<string, string> = {};
  for (let index = 0; index < patternParts.length; index++) {
    const patternPart: string = patternParts[index] ?? "";
    const pathPart: string = decodeURIComponent(pathParts[index] ?? "");
    if (patternPart.startsWith(":")) {
      params[patternPart.slice(1)] = pathPart;
    } else if (patternPart !== pathPart) {
      return null;
    }
  }

  return params;
}
